import { useEffect, useState } from "react";
import {
  getShareWatch,
  setShareWatch,
  type ShareScope,
} from "./api";

// Per-scope "notify me when anyone makes changes" toggle.  Mounted in
// surfaces that view a single scope (a group sidebar, an item detail
// pane).  One toggle covers every recipient of that scope — the user
// said it loud and clear: this is a property of the task or group, not
// of the person it's shared with.

export function ActivityWatchToggle({
  scope,
  scopeId,
  accessToken,
}: {
  scope: ShareScope;
  scopeId: string;
  accessToken: string;
}) {
  const [watching, setWatching] = useState<boolean | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const state = await getShareWatch(accessToken, scope, scopeId);
        if (alive) setWatching(state);
      } catch (err) {
        if (alive) setError(err instanceof Error ? err.message : String(err));
      }
    })();
    return () => {
      alive = false;
    };
  }, [accessToken, scope, scopeId]);

  const toggle = async () => {
    if (watching === null) return;
    const next = !watching;
    setBusy(true);
    setError(null);
    // Optimistic flip; revert on failure.
    setWatching(next);
    try {
      await setShareWatch(accessToken, scope, scopeId, next);
    } catch (err) {
      setWatching(!next);
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  if (watching === null) {
    // Don't flash an unset toggle while we load — the underlying state
    // dictates the label.
    return null;
  }

  return (
    <label className="flex items-center gap-2 text-[11px] text-slate-700 dark:text-slate-200">
      <input
        type="checkbox"
        checked={watching}
        onChange={() => void toggle()}
        disabled={busy}
        className="h-3.5 w-3.5 rounded border-slate-300 dark:border-slate-600"
      />
      <span>
        Notify me when anyone makes changes
        {scope === "group" ? " in this group" : ""}
      </span>
      {error && (
        <span className="text-[10px] text-rose-600 dark:text-rose-400">{error}</span>
      )}
    </label>
  );
}
